import com.github.chosamuel.kishibe.application.frameCount
import com.github.chosamuel.kishibe.math.Vector2
import com.github.chosamuel.kishibe.noise.simplex
import com.github.chosamuel.kishibe.primitives.Circle
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

data class SpringParticle(var pos: Vector2){
    var fixed = false;
}

class SpringBlob(
    val pos: Vector2,
    val radius: Double,
    val resolution: Int = 24
){
    val sp = circularSpringParticles(pos,radius,resolution)
    val center = SpringParticle(pos)
    val edgeForces = mutableListOf<Vector2>()
    val axisForces = mutableListOf<Vector2>()
    val cfg = createSpringConnection1D(sp)
    var TIME = 0.0
    init {
        repeat(resolution){
            edgeForces.add(Vector2(0.0,0.0))
            axisForces.add(Vector2(0.0,0.0))
        }
    }

    //CREATE LIST OF SPRING PARTICLES
    fun circularSpringParticles(
        pos: Vector2,
        radius: Double,
        resolution: Int = 24
    ): List<SpringParticle> {
        val step = PI * 2.0 / resolution.toDouble()
        return mutableListOf<SpringParticle>().apply {
            repeat(resolution) {
                val v = Vector2(cos(step * it), sin(step * it))
                    .times(radius)
                    .plus(pos)
                this.add(SpringParticle(v))
            }
        }
    }
    fun update(t: Double){
        TIME = t
        //edge particles
        val forces = getForces()
        val axialForces = getAxialForce()
        sp.forEachIndexed { i, it->
            edgeForces[i] += (forces[i] - edgeForces[i]) * 0.5
            edgeForces[i] = edgeForces[i] * 0.7
            axisForces[i] += (axialForces[i] - axisForces[i]) * 0.1
            axisForces[i] = axisForces[i] * 0.99
            sp[i].pos += edgeForces[i]
            sp[i].pos += axisForces[i]
        }
        val averageAxialForce = axisForces.reduce {
                a,v -> a+v
        }.div(sp.size.toDouble())
        center.pos -= averageAxialForce

        //resolveCollision()
    }
    fun getAxialForce(k: Double= 0.05, dampening: Double = 0.9): List<Vector2> {
        return mutableListOf<Vector2>().apply {
            sp.forEachIndexed { i, it->
                val percent = i.toDouble() / sp.size.toDouble()
                val theta = percent * PI * 2
                val noise = simplex(
                    cos(theta + frameCount),
                    sin(theta),
                     TIME * 0.005
                ) * sin(TIME * 0.005).let {
                    it*0.5+0.5
                }.let {
                    it*200.0
                }
                val x = it.pos.distanceTo(center.pos) - radius
                this.add(
                    pos
                        .minus(it.pos)
                        .normalized
                        .times(x + noise)
                    //.times(dampening)
                )
            }
        }
    }
    fun resolveCollision(){
        val spacing = (((PI*2.0) * radius) / resolution.toDouble()) * 0.25
        sp.forEach { me ->
            sp.forEach { other ->
                if(me != other){
                    val mec = Circle(me.pos,spacing)
                    val oc = Circle(other.pos,spacing)
                    if(mec.intersects(oc)){
                        val d = me.pos.distanceTo(other.pos)
                        val overlap = (d - mec.radius - oc.radius) * 0.5
                        me.pos -= me.pos.minus(other.pos).times(overlap).div(d)
                        other.pos += me.pos.minus(other.pos).times(overlap).div(d)
                    }
                }
            }
        }
    }

    fun getForces(k: Double = 0.05, dampening: Double = 0.9): List<Vector2>{
        return mutableListOf<Vector2>().apply {
            sp.forEachIndexed { i, p->
                var force = Vector2(0.0,0.0)
                cfg[i].forEach {
                    val x = p.pos.distanceTo(sp[it.index].pos) - it.restLength
                    force += sp[it.index].pos
                        .minus(p.pos)
                        .normalized
                        .times(x)
                }
                force /= cfg[i].size.toDouble()
                //force *= dampening
                this.add(force)
            }
        }
    }
}
